import { StatusTag } from "@/views/components/StatusTag";
import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Select, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { confirmDialog } from "@/utils/confirmDialog";
import { YES_OR_NO } from "@/meta/common";
import { useUserStore } from "@/stores/react/authStore";

export interface AdPageManagementRow {
    advert: {
        id: number;
        title?: string;
        classify?: number | string;
        type?: number | string;
        used?: number | string;
        pictureId?: number;
        content?: string;
        isNeedButton?: number | string;
        buttonType?: number | string;
        buttonName?: string;
        buttonValue?: string;
        createdTime?: string;
        updatedTime?: string;
    };
    user?: { name?: string };
}

export interface AdPageQuery {
    page: number;
    size: number;
    key?: string;
    startTime?: string;
    endTime?: string;
    classify?: number[];
    type?: number[];
    used?: number[];
}

export interface AdPageResult {
    list?: AdPageManagementRow[];
    total?: number;
}

export interface AdPageManagementPageProps {
    loadAdverts?: (query: AdPageQuery) => Promise<AdPageResult>;
    remove?: (id: number) => Promise<unknown>;
    permissions?: string[];
}

// 对齐 Vue ProTable/useTable 的默认分页契约，首次请求使用每页 10 条。
const defaultQuery: AdPageQuery = { page: 1, size: 10 };
const classifyOptions = [
    { label: "PC端", value: 1 },
    { label: "移动端", value: 2 }
];
const typeOptions = [
    { label: "图片", value: 1 },
    { label: "文本", value: 2 }
];
const usedOptions = [...YES_OR_NO.options.map(option => ({ label: option.label, value: Number(option.value) }))];

/** 复用 Vue 广告页分页接口。 */
export async function loadAdPageManagement(query: AdPageQuery): Promise<AdPageResult> {
    const response = await request.service.get<AdPageResult>("/dataService/admin/advert/paging", { params: query });
    return response.data;
}

/** 复用 Vue 广告页删除接口。 */
export async function deleteAdPageManagement(id: number): Promise<unknown> {
    const response = await request.service.delete(`/dataService/admin/advert/${id}`);
    return response.data;
}

/** 将广告页筛选表单转换为 Vue 分页请求参数。 */
export function buildAdPageQuery(values: {
    key?: string;
    classify?: number[];
    type?: number[];
    used?: number[];
    updatedTime?: [string, string];
}): AdPageQuery {
    const query: AdPageQuery = { ...defaultQuery };
    if (values.key) query.key = values.key;
    if (values.classify?.length) query.classify = values.classify;
    if (values.type?.length) query.type = values.type;
    if (values.used?.length) query.used = values.used;
    if (values.updatedTime?.[0] && values.updatedTime[1]) {
        query.startTime = `${values.updatedTime[0]} 00:00:00`;
        query.endTime = `${values.updatedTime[1]} 23:59:59`;
    }
    return query;
}

function formatDate(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

function formatClassify(value?: number | string): string {
    return classifyOptions.find(option => Number(option.value) === Number(value))?.label || DEFAULT_VALUE;
}

function formatType(value?: number | string): string {
    return typeOptions.find(option => Number(option.value) === Number(value))?.label || DEFAULT_VALUE;
}

function formatUsed(value?: number | string): string {
    return YES_OR_NO.getIntroByCode(value) || DEFAULT_VALUE;
}

/** 广告页管理列表，保留 Vue 嵌套字段、筛选和未使用操作条件。 */
export function AdPageManagementPage({
    loadAdverts = loadAdPageManagement,
    remove = deleteAdPageManagement,
    permissions
}: AdPageManagementPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 保持 Vue hasAuth 语义：广告页写权限独立控制新增、编辑和删除入口。
    const canWrite = (permissions ?? storePermissions).includes("ROLE_SS_AD_LIST_W");
    const [rows, setRows] = useState<AdPageManagementRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<AdPageQuery>(defaultQuery);
    const [key, setKey] = useState("");
    const [classify, setClassify] = useState<number[]>([]);
    const [type, setType] = useState<number[]>([]);
    const [used, setUsed] = useState<number[]>([]);
    const [updatedTime, setUpdatedTime] = useState<[string, string] | undefined>();
    const [reloadToken, setReloadToken] = useState(0);
    const [message, setMessage] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        void loadAdverts(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setMessage("广告页列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadAdverts, query, reloadToken]);

    const submitFilters = () => setQuery(buildAdPageQuery({ key, classify, type, used, updatedTime }));

    const resetFilters = () => {
        setKey("");
        setClassify([]);
        setType([]);
        setUsed([]);
        setUpdatedTime(undefined);
        setQuery(defaultQuery);
    };

    const runDelete = async (id: number) => {
        // 旧版广告页删除使用居中二次确认；取消时不得调用删除接口。
        if (!(await confirmDialog("是否删除广告页"))) return;
        try {
            await remove(id);
            setReloadToken(value => value + 1);
        } catch {
            setMessage("广告页删除失败");
        }
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 广告页列表的嵌套字段和写权限操作插槽。
    const columns: TableColumnProps<AdPageManagementRow>[] = [
        {
            key: "id",
            label: "编号",
            width: 148,
            prop: "advert",
            render: ({ row }) => (
                <Button
                    aria-label={`查看广告详情 ${row.advert.id}`}
                    type="link"
                    onClick={() => void navigate(`/userOperation/adPageManage/detail?id=${row.advert.id}`)}
                >
                    {row.advert.id}
                </Button>
            )
        },
        { key: "title", label: "标题", prop: "advert", render: ({ row }) => row.advert.title || DEFAULT_VALUE },
        {
            key: "classify",
            label: "分类",
            width: 204,
            prop: "advert",
            render: ({ row }) => formatClassify(row.advert.classify)
        },
        {
            key: "type",
            label: "广告类型",
            width: 420,
            prop: "advert",
            render: ({ row }) => formatType(row.advert.type)
        },
        {
            key: "user",
            label: "创建人",
            width: 148,
            prop: "user",
            render: ({ row }) => row.user?.name || DEFAULT_VALUE
        },
        {
            key: "updatedTime",
            label: "更新时间",
            width: 200,
            prop: "advert",
            render: ({ row }) => formatDate(row.advert.updatedTime)
        },
        {
            key: "used",
            label: "是否使用",
            width: 92,
            prop: "advert",
            render: ({ row }) => <StatusTag>{formatUsed(row.advert.used)}</StatusTag>
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 134,
            className: "compact",
            prop: "advert",
            render: ({ row }) =>
                canWrite && Number(row.advert.used) === 0 ? (
                    <Space>
                        <Button
                            aria-label={`编辑广告 ${row.advert.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => void navigate(`/userOperation/adBanner/adPageManage/edit?id=${row.advert.id}`)}
                        >
                            编辑
                        </Button>
                        <Button
                            aria-label={`删除广告 ${row.advert.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => void runDelete(row.advert.id)}
                        >
                            删除
                        </Button>
                    </Space>
                ) : null
        }
    ];

    return (
        <section aria-label="ad-page-management-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<AdPageManagementRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    rowKey={row => String(row.advert.id)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="mb-4" wrap>
                                {canWrite ? (
                                    <Button
                                        aria-label="新增广告页"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => void navigate("/userOperation/addAd")}
                                    >
                                        新增
                                    </Button>
                                ) : null}
                                <input
                                    aria-label="广告标题或编号"
                                    placeholder="请输入标题或编号搜索"
                                    value={key}
                                    onChange={event => setKey(event.target.value)}
                                />
                                <Select
                                    aria-label="广告分类"
                                    maxTagCount={1}
                                    mode="multiple"
                                    placeholder="请选择分类"
                                    value={classify}
                                    options={classifyOptions}
                                    onChange={value => setClassify(value)}
                                />
                                <Select
                                    aria-label="广告类型"
                                    maxTagCount={1}
                                    mode="multiple"
                                    placeholder="请选择广告类型"
                                    value={type}
                                    options={typeOptions}
                                    onChange={value => setType(value)}
                                />
                                <Select
                                    aria-label="是否使用"
                                    maxTagCount={1}
                                    mode="multiple"
                                    placeholder="是否使用"
                                    value={used}
                                    options={usedOptions}
                                    onChange={value => setUsed(value)}
                                />
                                <ZhDateRangePicker
                                    type="daterange"
                                    format="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    valueFormat="YYYY-MM-DD"
                                    startPlaceholder="更新时间开始"
                                    endPlaceholder="更新时间结束"
                                    value={updatedTime || ["", ""]}
                                    onChange={value =>
                                        setUpdatedTime(
                                            value ? [String(value[0] || ""), String(value[1] || "")] : undefined
                                        )
                                    }
                                />
                                <Button aria-label="查询广告" type="primary" onClick={submitFilters}>
                                    搜索
                                </Button>
                                <Button className="ad-page-reset-button" aria-label="重置广告" onClick={resetFilters}>
                                    重置
                                </Button>
                            </Space>

                            {message ? <Alert className="mb-4" message={message} role="alert" type="error" /> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={(page, size) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: size !== current.size ? 1 : page,
                                        size
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default AdPageManagementPage;
